import moment from 'moment';

/**
 * Takes the raw constraintMiddleware error message from iot-api and formats the UTC timestamp into a local time
 *
 * @param errorMessage - The raw error message from iot-api in following format
 * // You can not change the msisdn for this SIM till September 11th, 2024 at 1:14PM UTC
 *
 * @returns The formatted error message
 *
 * @example
 * ```
 * const localErrorMessage = formatTimeConstraintErrorMessage('You can not change the msisdn for this SIM till September 11th, 2024 at 1:14PM UTC');
 * console.log(localErrorMessage);
 * // You can not change the msisdn for this SIM till September 11th, 2024 at 1:14PM
 * ```
 */
export function formatTimeConstraintErrorMessage(errorMessage: string) {
  const [message, utcTimestamp] = errorMessage.split('till ');

  const [utcDate, utcTime] = utcTimestamp
    .replace('UTC', '')
    .trim()
    .split(' at ');

  const utcMoment = moment.utc(`${utcDate} ${utcTime}`, 'MMMM D, YYYY h:mm A');

  return `${message} till ${utcMoment
    .local()
    .format('MMMM DD, YYYY [at] h:mm A')}`;
}

/**
 * Downloads a file from a Blob object.
 *
 * @param {Blob} blob - The Blob object containing the file data.
 * @param {string} fileName - The desired name for the downloaded file with file extension.
 *
 * @description
 * This function creates a temporary URL for the Blob, creates an invisible anchor element,
 * sets its href to the Blob URL, triggers a click to start the download, and then revokes the URL.
 *
 * @example
 * // Assuming you have a Blob object
 * const myBlob = new Blob(['Hello, world!'], {type: 'text/plain'});
 * downloadFileFromBlob(myBlob, 'hello.txt');
 */
export function downloadFileFromBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchorEl = document.createElement('a');

  anchorEl.href = url;
  anchorEl.download = fileName;
  anchorEl.click();

  URL.revokeObjectURL(url);

  anchorEl.remove();
}

/**
 * Downloads a CSV file from a text string.
 *
 * @param {string} text - The CSV content as a string.
 * @param {string} fileName - The desired name for the downloaded CSV file.
 *
 * @description
 * This function converts a text string containing CSV data into a Blob object
 * and triggers a file download using the downloadFileFromBlob helper function.
 * The downloaded file will have the MIME type 'text/csv'.
 *
 * @example
 * // Download a simple CSV string
 * const csvContent = 'name,age\nJohn,30\nJane,25';
 * downloadCSVFromText(csvContent, 'users.csv');
 */
export function downloadCSVFromText(text: string, fileName: string) {
  const blob = new Blob([text], { type: 'text/csv' });
  downloadFileFromBlob(blob, fileName);
}
